import React, { useCallback, useState } from 'react';
import { useHistory, useLocation, useUrlBuilder } from '@app/router';
import { useIsNonInteractiveViewer } from '@app/ui';
import { useUserIsLoggedIn } from '@app/user';
import { navigateToBrowsePageWithFilters } from '@app/catalog';

import Logo from '@app/shell/SiteHeader/shared/components/Logo';
import { MobileInboxIcon } from '@app/inbox';
import { SearchIcon } from '@stockx/icons';
import MobileHeaderWrapper from '@app/shell/SiteHeader/shared/components/MobileHeaderWrapper';
import useMobileNavOpen from '@app/shell/SiteHeader/shared/hooks/useMobileNavOpen';
import { EXPERIMENTS } from '@app/config';
import { useExperiment } from '@app/experiments';
import { Events, useGATrackEvent } from '@app/analytics';
import { usePageType } from '@app/page';
import MobileNav from './components/MobileNav';
import Search from './components/Search';
import { MobileSearchToggle } from './ContentMobile.styles';

const StaticContentMobile = () => (
  <MobileHeaderWrapper isOpen={false} handleOnClickToggle={() => {}}>
    <Logo />
    <MobileNav />
  </MobileHeaderWrapper>
);

const SiteContentMobile = () => {
  const history = useHistory();
  const { pathname, search } = useLocation();
  const buildUrl = useUrlBuilder();
  const { isOpen, handleToggle } = useMobileNavOpen();
  const trackGAEvent = useGATrackEvent();
  const pageType = usePageType();

  const loggedIn = useUserIsLoggedIn();

  const [showMobileSearch, setMobileSearchClick] = useState(
    pathname?.includes('/search'),
  );

  const [isNavRendered, setIsNavRendered] = useState(false);

  const abCrmPromoFlag = useExperiment(
    EXPERIMENTS.ab_pirate_crm_promo_web,
  ) as string;

  const isAbCrmPromoVariant = abCrmPromoFlag === 'true';

  // For mobile, only display icon if logged in (compared to desktop header)
  const displayMessageCenterIcon = loggedIn || isAbCrmPromoVariant;

  const handleMobileSearchClick = useCallback(() => {
    setMobileSearchClick(!showMobileSearch);
    if (showMobileSearch && pathname?.includes('/search')) {
      navigateToBrowsePageWithFilters(pathname, search, history, buildUrl);
    }
  }, [showMobileSearch, pathname]);

  const handleMobileNavClick = useCallback(() => {
    trackGAEvent(Events.HamburgerMenuOpened, pageType);
    setIsNavRendered(true);
    handleToggle();
  }, [handleToggle, trackGAEvent, pageType]);

  return (
    <MobileHeaderWrapper
      isOpen={isOpen}
      handleOnClickToggle={handleMobileNavClick}
    >
      {showMobileSearch ? (
        <Search handleMobileSearchClick={handleMobileSearchClick} />
      ) : (
        <>
          <Logo />

          {displayMessageCenterIcon && <MobileInboxIcon />}

          <MobileSearchToggle
            className="search-toggle"
            id="mobile-search-toggle"
            onClick={handleMobileSearchClick}
          >
            <SearchIcon />
          </MobileSearchToggle>
        </>
      )}

      {isNavRendered ? <MobileNav /> : null}
    </MobileHeaderWrapper>
  );
};

const ContentMobile = () => {
  const isNonInteractiveViewer = useIsNonInteractiveViewer();

  if (isNonInteractiveViewer) {
    return <StaticContentMobile />;
  }

  return <SiteContentMobile />;
};

export default ContentMobile;
